import { confirmCost } from "@/components/cost-confirm";
import { pdfIndir } from "@/lib/pdf-indir";
import { useState, useMemo, useRef, useEffect, type ReactNode } from "react";
import { useListDomains } from "@workspace/api-client-react";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { useOnay } from "@/components/ui/onay-dialog";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip } from "recharts";
import { useActiveDomain } from "@/hooks/use-active-domain";
import { Link, useRoute, useLocation } from "wouter";
import {
  Globe, RefreshCw, Loader2, Check, FileText, Search, Target, ExternalLink,
  Sparkles, Zap, Trophy, Link2, MousePointerClick, Eye, Percent, ArrowDownUp, AlertTriangle, ChevronDown,
} from "lucide-react";

type StrData = {
  pageUrl: string; pageTitle: string; primaryKeyword: string;
  secondaryKeywords: string[];
  aiSuggestion: string;
  existingLinks: { incoming: { url: string; anchor: string }[]; outgoing: { url: string; anchor: string }[] };
  gsc: { clicks: number; impressions: number; ctr: number; position: number; queries: { query: string; clicks: number; impressions: number; ctr: number; position: number }[] };
  aiOverview: { question: string; position: number; totalSources: number; aiSearchVolume: number }[];
  competitors: { domain: string; commonKeywords: number; avgPosition: number }[];
  contentGaps: { keyword: string; volume: number; covered: boolean }[];
  cluster: { name: string; pillarUrl: string | null; siblings: { url: string; title: string }[] } | null;
  paa: string[];
  relatedSearches: string[];
  aiOverviewAnswer: { answer: string; sources: { domain: string; url: string }[]; weAreCited: boolean };
  aiEngines: { engine: string; appears: boolean }[];
  engineAnswers: { engine: string; answer: string }[];
  pageLanguage: string;
  keywordVolume: number;
  keywordCompetitors: { domain: string; url: string; title: string; position: number; estimatedTraffic: number; isUs: boolean }[];
  warnings: { type: string; label: string; severity: string; message: string; anchor: string; targetUrl: string; original: string; suggested: string }[];
  competitiveAnalysis: string;
  metaDescription: string;
  metaBoost: { suggestedTitle: string; suggestedDescription: string; rationale: string } | null;
  auditReport: { passed: boolean; score: number; issues: { severity: "critical" | "minor"; type: string; message: string }[]; fixApplied: boolean } | null;
  brief: {
    intent: string; targetQueries: string[];
    outline: { h2: string; h3: string[] }[];
    faq: string[]; internalLinks: { anchor: string; url: string }[];
    mustInclude: string[]; trustSignalIdeas: string[]; competitorAngles: string[]; wordTarget: number;
  } | null;
  internalLinks: { incoming: number; outgoing: number };
  linkOpportunities: { fromUrl: string; fromTitle: string; anchor: string; reason: string }[];
  outboundLinks: { url: string; anchor: string; reason: string }[];
  cannibalization: { query: string; pages: { url: string; position: number; clicks: number }[] }[];
  existingContent: string;
  summary: string;
  plan: { priority: "high" | "medium" | "low"; category: string; action: string }[];
  revisedContent: string;
};
type StrResp = { configured: boolean; data: StrData | null; runAt: string | null; running?: boolean; runError?: string | null };
type PageItem = { id: number; url: string; title: string | null; primaryKeyword: string | null };

const C = { cyan: "#e83e3c", green: "#12b76a", red: "#f04438", amber: "#f79009", purple: "#e83e3c", blue: "#ec6461" };
const fmt = (n: number) => (n >= 10000 ? `${(n / 1000).toFixed(0)}k` : n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(Math.round(n)));
const shortUrl = (u: string) => (u || "").replace(/^https?:\/\/[^/]+/, "").replace(/#:~:.*$/, "").replace(/\/$/, "") || "/";

// ── Markdown render: **bold** + [link](url) + ==revize işareti== + başlık/madde ──
function boldParts(t: string, base: number) {
  return t.split(/\*\*(.+?)\*\*/g).map((p, i) => (i % 2 === 1 ? <b key={`${base}b${i}`}>{p}</b> : <span key={`${base}s${i}`}>{p}</span>));
}
function linksAndBold(t: string) {
  const out: ReactNode[] = [];
  // Görsel VE link tek geçişte: ![alt](url) paragrafın NERESİNDEYSE görsel ORADA basılır
  const linkRe = /(!?)\[([^\]]*)\]\((https?:\/\/[^)\s]+)\)/g;
  let last = 0, k = 0; let m: RegExpExecArray | null;
  while ((m = linkRe.exec(t)) !== null) {
    if (m.index > last) out.push(...boldParts(t.slice(last, m.index), k));
    k += 100;
    if (m[1] === "!") out.push(<img key={`i${k}`} src={m[3]} alt={m[2]} loading="lazy" className="rounded-lg my-2 max-w-full" />);
    else out.push(<a key={`l${k}`} href={m[3]} target="_blank" rel="noopener noreferrer" style={{ color: C.cyan }} className="underline decoration-dotted">{m[2]}</a>);
    last = m.index + m[0].length;
  }
  if (last < t.length) out.push(...boldParts(t.slice(last), k + 100));
  return out;
}
// Önce ==işaret== bölümlerini ayır; işaretli kısımları <mark> ile vurgula (revize gösterimi).
function inline(t: string) {
  const out: ReactNode[] = [];
  const re = /==(.+?)==/g;
  let last = 0, k = 0; let m: RegExpExecArray | null;
  while ((m = re.exec(t)) !== null) {
    if (m.index > last) out.push(...linksAndBold(t.slice(last, m.index)));
    k += 1;
    out.push(<mark key={`mk${k}`} style={{ background: "rgba(34,197,94,0.22)", color: "#166534", borderRadius: 3, padding: "0 2px", boxDecorationBreak: "clone", WebkitBoxDecorationBreak: "clone" }}>{linksAndBold(m[1])}</mark>);
    last = m.index + m[0].length;
  }
  if (last < t.length) out.push(...linksAndBold(t.slice(last)));
  return out;
}
const isTableSepL = (s: string) => /^\|?[\s:]*-{2,}[\s:|-]*\|?$/.test(s) && s.includes("-");
const rowCellsL = (s: string) => s.trim().replace(/^\||\|$/g, "").split("|").map((c) => c.trim());
function MiniMarkdown({ text }: { text: string }) {
  const lines = (text || "").split("\n");
  const els: ReactNode[] = [];
  for (let i = 0; i < lines.length; i++) {
    const t = lines[i].trim();
    if (!t) continue;
    // Görsel satırı → mevcut görsel ORİJİNAL URL'iyle, olduğu gibi yerinde gösterilir
    const img = t.match(/^!\[([^\]]*)\]\(([^)\s]+)\)$/);
    if (img) { els.push(<img key={i} src={img[2]} alt={img[1]} loading="lazy" className="rounded-lg my-2 max-w-full" />); continue; }
    // Tablo bloğu → gerçek <table>
    if (t.includes("|") && i + 1 < lines.length && isTableSepL(lines[i + 1].trim())) {
      const header = rowCellsL(t); i += 2; const body: string[][] = [];
      while (i < lines.length && lines[i].trim().includes("|") && lines[i].trim()) { body.push(rowCellsL(lines[i].trim())); i++; }
      i--;
      els.push(
        <div key={`tb${i}`} className="overflow-x-auto my-2">
          <table className="w-full text-xs border-collapse">
            <thead><tr>{header.map((h, j) => <th key={j} className="border px-2 py-1 text-left font-semibold" style={{ borderColor: "rgba(0,0,0,0.12)", background: "rgba(0,0,0,0.04)" }}>{inline(h)}</th>)}</tr></thead>
            <tbody>{body.map((r, ri) => <tr key={ri}>{r.map((c, ci) => <td key={ci} className="border px-2 py-1" style={{ borderColor: "rgba(0,0,0,0.08)" }}>{inline(c)}</td>)}</tr>)}</tbody>
          </table>
        </div>
      );
      continue;
    }
    // Alıntı bloğu → açıklama/uzman kutusu (Healthline tarzı; kavram + uzman görüşü kuralı)
    if (t.startsWith(">")) {
      const box: string[] = [];
      while (i < lines.length && lines[i].trim().startsWith(">")) { box.push(lines[i].trim().replace(/^>\s?/, "")); i++; }
      i--;
      els.push(
        <div key={`bq${i}`} className="my-2.5 rounded-lg border-l-4 px-3.5 py-2.5" style={{ borderColor: "#f59e0b", background: "rgba(245,158,11,0.07)" }}>
          {box.filter(Boolean).map((b, j) => <p key={j} className="text-[13px] leading-relaxed">{inline(b)}</p>)}
        </div>
      );
      continue;
    }
    if (t.startsWith("### ")) els.push(<h4 key={i} className="font-semibold mt-2">{inline(t.slice(4))}</h4>);
    else if (t.startsWith("## ")) els.push(<h3 key={i} className="font-bold text-base mt-3">{inline(t.slice(3))}</h3>);
    else if (t.startsWith("# ")) els.push(<h2 key={i} className="font-bold text-lg mt-2">{inline(t.slice(2))}</h2>);
    else if (/^[-*]\s/.test(t)) els.push(<div key={i} className="flex gap-2 pl-2"><span className="text-muted-foreground">•</span><span>{inline(t.replace(/^[-*]\s/, ""))}</span></div>);
    else if (/^\d+\.\s/.test(t)) els.push(<div key={i} className="pl-2">{inline(t)}</div>);
    else els.push(<p key={i}>{inline(t)}</p>);
  }
  return <div className="space-y-1.5 text-sm leading-relaxed">{els}</div>;
}

// ── Markdown → HTML (== işaretleri temizlenir) — Word/HTML export + kopya için ──
const esc = (s: string) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
function inlineHtml(t: string): string {
  let s = t.replace(/==(.+?)==/g, "$1"); // revize işaretlerini at
  s = esc(s);
  // Satır içi görseller ORİJİNAL URL'leriyle korunur (sunucuya indirilmez)
  s = s.replace(/!\[([^\]]*)\]\((https?:\/\/[^)\s]+)\)/g, '<img src="$2" alt="$1" style="max-width:100%">');
  s = s.replace(/\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)/g, '<a href="$2">$1</a>');
  s = s.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
  return s;
}
const isTableSep = (s: string) => /^\|?[\s:]*-{2,}[\s:|-]*\|?$/.test(s) && s.includes("-");
const rowCells = (s: string) => s.trim().replace(/^\||\|$/g, "").split("|").map((c) => c.trim());
function mdToHtml(md: string): string {
  const lines = (md || "").split("\n");
  const out: string[] = [];
  let inList = false;
  const closeList = () => { if (inList) { out.push("</ul>"); inList = false; } };
  for (let i = 0; i < lines.length; i++) {
    const t = lines[i].trim();
    if (!t) { closeList(); continue; }
    // Markdown tablo bloğu: başlık satırı + ayraç + satırlar → gerçek <table>
    if (t.includes("|") && i + 1 < lines.length && isTableSep(lines[i + 1].trim())) {
      closeList();
      const header = rowCells(t);
      i += 2;
      const body: string[][] = [];
      while (i < lines.length && lines[i].trim().includes("|") && lines[i].trim()) { body.push(rowCells(lines[i].trim())); i++; }
      i--;
      out.push('<table border="1" cellspacing="0" cellpadding="6" style="border-collapse:collapse;width:100%">');
      out.push(`<thead><tr>${header.map((h) => `<th style="background:#f2f2f2;text-align:left">${inlineHtml(h)}</th>`).join("")}</tr></thead>`);
      out.push(`<tbody>${body.map((r) => `<tr>${r.map((c) => `<td>${inlineHtml(c)}</td>`).join("")}</tr>`).join("")}</tbody></table>`);
      continue;
    }
    const imgH = t.match(/^!\[([^\]]*)\]\(([^)\s]+)\)$/);
    if (imgH) { closeList(); out.push(`<p><img src="${imgH[2]}" alt="${esc(imgH[1])}" style="max-width:100%"></p>`); continue; }
    if (t.startsWith(">")) {
      // Kavram/uzman kutusu → stilli blockquote (Word ve HTML yapıştırmada kutu görünür)
      closeList();
      const box: string[] = [];
      while (i < lines.length && lines[i].trim().startsWith(">")) { box.push(lines[i].trim().replace(/^>\s?/, "")); i++; }
      i--;
      out.push(`<blockquote style="margin:12px 0;padding:10px 14px;border-left:4px solid #f59e0b;background:#fdf6ec">${box.filter(Boolean).map((b) => `<p style="margin:4px 0">${inlineHtml(b)}</p>`).join("")}</blockquote>`);
    }
    else if (t.startsWith("### ")) { closeList(); out.push(`<h3>${inlineHtml(t.slice(4))}</h3>`); }
    else if (t.startsWith("## ")) { closeList(); out.push(`<h2>${inlineHtml(t.slice(3))}</h2>`); }
    else if (t.startsWith("# ")) { closeList(); out.push(`<h1>${inlineHtml(t.slice(2))}</h1>`); }
    else if (/^[-*]\s/.test(t)) { if (!inList) { out.push("<ul>"); inList = true; } out.push(`<li>${inlineHtml(t.replace(/^[-*]\s/, ""))}</li>`); }
    else { closeList(); out.push(`<p>${inlineHtml(t)}</p>`); }
  }
  closeList();
  return out.join("\n");
}
// ── Aranabilir sayfa seçici ──
function PageSearchSelect({ pages, value, onChange }: { pages: PageItem[]; value: number | null; onChange: (id: number) => void }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    function onDoc(e: MouseEvent) { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);
  const selected = pages.find((p) => p.id === value);
  const filtered = useMemo(() => {
    const qq = q.trim().toLowerCase();
    if (!qq) return pages;
    return pages.filter((p) => `${p.url} ${p.title ?? ""} ${p.primaryKeyword ?? ""}`.toLowerCase().includes(qq));
  }, [pages, q]);
  return (
    <div className="relative w-full sm:w-[460px]" ref={ref}>
      <button onClick={() => setOpen((o) => !o)} className="flex items-center gap-2 w-full h-10 px-3 rounded-md border bg-transparent text-sm text-left" style={{ borderColor: "rgba(0,0,0,0.12)" }}>
        <FileText className="w-4 h-4 shrink-0 opacity-70" />
        <span className={`flex-1 truncate ${selected ? "" : "text-muted-foreground"}`}>{selected ? shortUrl(selected.url) : "Güçlendirilecek sayfayı seç / ara"}</span>
        <ChevronDown className="w-4 h-4 shrink-0 opacity-50" />
      </button>
      {open && (
        <div className="absolute z-50 mt-1 w-full rounded-md border shadow-xl" style={{ borderColor: "rgba(0,0,0,0.12)", background: "#ffffff" }}>
          <div className="p-2 border-b" style={{ borderColor: "rgba(0,0,0,0.08)" }}>
            <div className="flex items-center gap-2 px-2 h-8 rounded bg-black/5">
              <Search className="w-3.5 h-3.5 opacity-50" />
              <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Sayfa ara… (url / başlık / kelime)" className="flex-1 bg-transparent text-sm outline-none" />
            </div>
          </div>
          <div className="max-h-[320px] overflow-y-auto py-1">
            {filtered.length === 0 && <div className="px-3 py-4 text-xs text-muted-foreground text-center">Eşleşen sayfa yok</div>}
            {filtered.map((p) => (
              <button key={p.id} onClick={() => { onChange(p.id); setOpen(false); setQ(""); }} className="flex flex-col items-start w-full px-3 py-1.5 text-left hover:bg-black/5" style={p.id === value ? { background: "rgba(139,92,246,0.12)" } : undefined}>
                <span className="text-sm truncate w-full">{shortUrl(p.url)}</span>
                {(p.title || p.primaryKeyword) && <span className="text-[11px] text-muted-foreground truncate w-full">{p.primaryKeyword || p.title}</span>}
              </button>
            ))}
          </div>
          <div className="px-3 py-1.5 text-[11px] text-muted-foreground border-t" style={{ borderColor: "rgba(0,0,0,0.08)" }}>{filtered.length} / {pages.length} sayfa</div>
        </div>
      )}
    </div>
  );
}

// ── Sayfaya özel CANLI GSC paneli: 24s-16ay aralık + metrik grafiği + filtrelenebilir sorgu tablosu ──
type PageGsc = { connected: boolean; range?: string; totals: { clicks: number; impressions: number; ctr: number; position: number } | null; trend: { date: string; clicks: number; impressions: number; ctr: number; position: number }[]; queries: { query: string; clicks: number; impressions: number; ctr: number; position: number }[] };
const RANGES: { k: string; l: string }[] = [
  { k: "24h", l: "24 saat" }, { k: "7d", l: "7 gün" }, { k: "28d", l: "28 gün" }, { k: "3m", l: "3 ay" }, { k: "6m", l: "6 ay" }, { k: "12m", l: "12 ay" }, { k: "16m", l: "16 ay" },
];
function PageGscPanel({ domainId, pageId }: { domainId: number | null; pageId: number | null }) {
  const [range, setRange] = useState("28d");
  const [metrics, setMetrics] = useState<Set<string>>(new Set(["clicks", "impressions"]));
  const [filter, setFilter] = useState("");
  const [qsort, setQsort] = useState<{ k: keyof PageGsc["queries"][number]; dir: 1 | -1 }>({ k: "clicks", dir: -1 });
  const toggle = (m: string) => setMetrics((s) => { const n = new Set(s); n.has(m) ? n.delete(m) : n.add(m); return n.size ? n : s; });
  const { data, isFetching } = useQuery({
    queryKey: ["page-gsc", domainId, pageId, range],
    enabled: !!domainId && !!pageId,
    queryFn: async (): Promise<PageGsc> => {
      try { const r = await fetch(`/api/domains/${domainId}/page-gsc?pageId=${pageId}&range=${range}`); return r.ok ? await r.json() : { connected: false, totals: null, trend: [], queries: [] }; }
      catch { return { connected: false, totals: null, trend: [], queries: [] }; }
    },
  });
  if (!data?.connected) return null;
  const t = data.totals ?? { clicks: 0, impressions: 0, ctr: 0, position: 0 };
  const cards = [
    { key: "clicks", label: "Toplam Tıklama", color: "#4285F4", val: fmt(t.clicks) },
    { key: "impressions", label: "Toplam Gösterim", color: "#8E44AD", val: fmt(t.impressions) },
    { key: "ctr", label: "Ortalama TO", color: "#00A36C", val: `%${(t.ctr * 100).toFixed(1)}` },
    { key: "position", label: "Ortalama Konum", color: "#F5A623", val: t.position > 0 ? t.position.toFixed(1) : "—" },
  ];
  const qf = filter.trim().toLowerCase();
  const queries = data.queries.filter((q) => !qf || q.query.toLowerCase().includes(qf)).sort((a, b) => ((a[qsort.k] as number) - (b[qsort.k] as number)) * qsort.dir);
  const setSort = (k: keyof PageGsc["queries"][number]) => setQsort((s) => s.k === k ? { k, dir: (s.dir === 1 ? -1 : 1) as 1 | -1 } : { k, dir: -1 });
  return (
    <Card className="mb-4" style={{ borderColor: "rgba(66,133,244,0.25)" }}><CardContent className="p-4">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <div className="flex items-center gap-2"><Search className="w-4 h-4" style={{ color: "#4285F4" }} /><span className="font-semibold text-sm">Arama Performansı</span>{isFetching && <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground" />}</div>
        <div className="flex items-center rounded-lg border p-0.5 ps-no-print" style={{ borderColor: "rgba(0,0,0,0.12)" }}>
          {RANGES.map((r) => <button key={r.k} onClick={() => setRange(r.k)} className="text-[11px] px-2 py-1 rounded-md transition" style={range === r.k ? { background: "rgba(66,133,244,0.2)", color: "#b12220" } : { color: "#70706c" }}>{r.l}</button>)}
        </div>
      </div>
      {/* Metrik kartları (tıkla = grafikte aç/kapa) */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
        {cards.map((m) => { const on = metrics.has(m.key); return (
          <button key={m.key} onClick={() => toggle(m.key)} className="text-left rounded-lg p-3 border transition" style={{ borderColor: on ? m.color : "rgba(0,0,0,0.08)", background: on ? `${m.color}14` : "transparent", opacity: on ? 1 : 0.55 }}>
            <div className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full" style={{ background: m.color }} /><span className="text-[11px] text-muted-foreground">{m.label}</span></div>
            <div className="text-xl font-bold tabular-nums mt-1" style={{ color: on ? m.color : "#1d1d1b" }}>{m.val}</div>
          </button>
        ); })}
      </div>
      {/* Büyük grafik */}
      <div className="h-72">
        {data.trend.length ? (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data.trend} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#70706c" }} tickMargin={8} minTickGap={24} />
              <YAxis yAxisId="clicks" hide /><YAxis yAxisId="impressions" hide />
              <YAxis yAxisId="ctr" hide domain={[0, (mx: number) => mx * 1.6]} /><YAxis yAxisId="position" hide reversed domain={[1, "dataMax"]} />
              <RTooltip contentStyle={{ background: "#ffffff", border: "1px solid rgba(0,0,0,0.12)", borderRadius: 8, fontSize: 12 }} labelStyle={{ color: "#1d1d1b" }} formatter={(v: number, n: string) => [n === "Ort. TO" ? `%${v.toFixed(1)}` : n === "Ort. Konum" ? v.toFixed(1) : fmt(v), n]} />
              {metrics.has("clicks") && <Line yAxisId="clicks" type="monotone" dataKey="clicks" name="Tıklama" stroke="#4285F4" strokeWidth={2} dot={false} />}
              {metrics.has("impressions") && <Line yAxisId="impressions" type="monotone" dataKey="impressions" name="Gösterim" stroke="#8E44AD" strokeWidth={2} dot={false} />}
              {metrics.has("ctr") && <Line yAxisId="ctr" type="monotone" dataKey="ctr" name="Ort. TO" stroke="#00A36C" strokeWidth={2} dot={false} />}
              {metrics.has("position") && <Line yAxisId="position" type="monotone" dataKey="position" name="Ort. Konum" stroke="#F5A623" strokeWidth={2} dot={false} />}
            </LineChart>
          </ResponsiveContainer>
        ) : <div className="h-full flex items-center justify-center text-sm text-muted-foreground">Bu aralıkta veri yok (24 saat seçeneği veri gecikmesinden boş olabilir).</div>}
      </div>
      {/* Filtrelenebilir sorgu tablosu */}
      <div className="mt-3">
        <div className="flex items-center gap-2 mb-2 ps-no-print">
          <div className="flex items-center gap-2 px-2 h-8 rounded bg-black/5 flex-1 max-w-xs"><Search className="w-3.5 h-3.5 opacity-50" /><input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Sorgu ara/filtrele…" className="flex-1 bg-transparent text-sm outline-none" /></div>
          <span className="text-[11px] text-muted-foreground">{queries.length} sorgu</span>
        </div>
        <div className="overflow-x-auto max-h-[360px] overflow-y-auto">
          <table className="w-full text-sm min-w-[520px]">
            <thead className="sticky top-0" style={{ background: "#ffffff" }}><tr className="text-[11px] text-muted-foreground text-left border-b" style={{ borderColor: "rgba(0,0,0,0.08)" }}>
              <th className="py-1.5">Sorgu</th>
              {(["clicks", "impressions", "ctr", "position"] as const).map((k) => <th key={k} className="py-1.5 text-right cursor-pointer select-none" onClick={() => setSort(k)}>{{ clicks: "Tıklama", impressions: "Gösterim", ctr: "TO", position: "Poz." }[k]}{qsort.k === k ? (qsort.dir === -1 ? " ↓" : " ↑") : ""}</th>)}
            </tr></thead>
            <tbody>{queries.map((q, i) => (
              <tr key={i} className="border-b last:border-0" style={{ borderColor: "rgba(0,0,0,0.05)" }}>
                <td className="py-1.5 pr-3">{q.query}</td>
                <td className="py-1.5 text-right tabular-nums font-medium">{fmt(q.clicks)}</td>
                <td className="py-1.5 text-right tabular-nums text-muted-foreground">{fmt(q.impressions)}</td>
                <td className="py-1.5 text-right tabular-nums text-muted-foreground">%{(q.ctr * 100).toFixed(1)}</td>
                <td className="py-1.5 text-right tabular-nums" style={{ color: q.position <= 3 ? C.green : q.position <= 10 ? C.amber : "#a3a3a0" }}>{q.position.toFixed(1)}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      </div>
    </CardContent></Card>
  );
}

export default function PageStrengthen() {
  const { data: domains } = useListDomains();
  const [activeDomainId, setActiveDomainId] = useActiveDomain(domains);
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { onayIste, OnayPenceresi } = useOnay();
  // pageId ROTADAN gelir: /page-strengthen = kart listesi, /page-strengthen/:pid = detay sayfası
  const [, routeParams] = useRoute("/page-strengthen/:pid");
  const [, setLocation] = useLocation();
  const pageId = routeParams?.pid ? Number(routeParams.pid) : null;
  const setPageId = (id: number | null) => setLocation(id ? `/page-strengthen/${id}` : "/page-strengthen");
  const [copied, setCopied] = useState<"" | "md" | "html" | "title" | "desc">("");
  const [editMode, setEditMode] = useState(false);
  const [agentSearch, setAgentSearch] = useState("");   // ajan paneli sayfa arama
  const [selPid, setSelPid] = useState<number | null>(null); // ajan panelinde seçilen sayfa
  const [edited, setEdited] = useState<string | null>(null);
  // MANUEL REVİZE: kullanıcı serbest talimat yazar, motor kayıtlı revizeyi buna göre günceller
  const [revizeTalimat, setRevizeTalimat] = useState("");
  const [revizeBusy, setRevizeBusy] = useState(false);

  /* ── UYGULAMA KOLU (Faz 6): revizeyi WordPress'e uygula + geri al ── */
  const [wpBusy, setWpBusy] = useState(false);
  /* Önerilen başlık/meta ELLE düzenleme — null = dokunulmadı (öneri gösterilir) */
  const [mbTitle, setMbTitle] = useState<string | null>(null);
  const [mbDesc, setMbDesc] = useState<string | null>(null);
  const [mbBusy, setMbBusy] = useState(false);
  useEffect(() => { setMbTitle(null); setMbDesc(null); }, [pageId]); // sayfa değişince taslak düzenleme sıfırlanır
  const mbKaydet = async (oneriTitle: string, oneriDesc: string) => {
    if (!activeDomainId || !pageId || mbBusy) return;
    setMbBusy(true);
    try {
      const r = await fetch(`/api/domains/${activeDomainId}/page-strengthen/meta-duzenle`, {
        method: "PATCH", headers: { "content-type": "application/json" },
        body: JSON.stringify({ pageId, title: mbTitle ?? oneriTitle, description: mbDesc ?? oneriDesc }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error ?? "Kaydedilemedi");
      toast({ title: "Kaydedildi ✓", description: "Siteye Uygula'da bu hâli kullanılacak." });
      setMbTitle(null); setMbDesc(null);
      queryClient.invalidateQueries({ queryKey: ["page-strengthen", pageId] });
    } catch (e) { toast({ title: "Kaydedilemedi", description: String(e instanceof Error ? e.message : e), variant: "destructive" }); }
    finally { setMbBusy(false); }
  };

  const wpDurum = useQuery<{ bagli: boolean; uygulamalar: { id: number; pageId: number | null; tur: string; durum: string; detay?: { wpLink?: string } | null }[] }>({
    queryKey: ["wp-durum", activeDomainId],
    enabled: !!activeDomainId,
    staleTime: 60_000,
    queryFn: async () => (await fetch(`/api/domains/${activeDomainId}/wp`)).json(),
  });
  const sonWpUygulama = wpDurum.data?.uygulamalar?.find((u) => u.pageId === pageId && u.tur === "revize" && u.durum === "uygulandi");
  const wpUygula = async () => {
    if (!activeDomainId || !pageId || wpBusy) return;
    if (!(await onayIste({ baslik: "Revize siteye uygulansın mı?", aciklama: "Başlık + içerik + SEO meta, WordPress'teki sayfanın üzerine yazılacak. Uygulamadan önce canlı içerik yedeklenir — tek tıkla geri alabilirsin.", onayYazisi: "Siteye Uygula" }))) return;
    setWpBusy(true);
    try {
      const r = await fetch(`/api/domains/${activeDomainId}/wp/uygula-revize`, {
        method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ pageId }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error ?? "Uygulanamadı");
      toast({ title: "Siteye uygulandı ✓", description: j.mod === "yerinde"
        ? `Sayfa tema yapıcısıyla kurulu — revize, şablon bozulmadan ana içerik alanına yerleştirildi.${j.metaGuncellendi ? " SEO meta dahil." : ""}`
        : `${j.metaGuncellendi ? "Meta dahil güncellendi." : "İçerik güncellendi; SEO meta alanını site kabul etmedi (Rank Math/Yoast REST kapalı olabilir) — meta'yı elle girin."}` });
      void queryClient.invalidateQueries({ queryKey: ["wp-durum", activeDomainId] });
    } catch (e) { toast({ title: "Uygulanamadı", description: String(e instanceof Error ? e.message : e), variant: "destructive" }); }
    finally { setWpBusy(false); }
  };
  const wpGeriAl = async () => {
    if (!activeDomainId || !sonWpUygulama || wpBusy) return;
    if (!(await onayIste({ baslik: "Geri alınsın mı?", aciklama: "Sayfa, uygulamadan önceki yedeğine birebir geri döndürülecek.", onayYazisi: "Geri Al", tehlikeli: true }))) return;
    setWpBusy(true);
    try {
      const r = await fetch(`/api/domains/${activeDomainId}/wp/geri-al`, {
        method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ uygulamaId: sonWpUygulama.id }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error ?? "Geri alınamadı");
      toast({ title: "Geri alındı ✓", description: "Sayfa uygulamadan önceki hâline döndü." });
      void queryClient.invalidateQueries({ queryKey: ["wp-durum", activeDomainId] });
    } catch (e) { toast({ title: "Geri alınamadı", description: String(e instanceof Error ? e.message : e), variant: "destructive" }); }
    finally { setWpBusy(false); }
  };
  const [revizeMesaj, setRevizeMesaj] = useState<string | null>(null);
  const manuelRevizeUygula = async () => {
    if (!pageId || revizeBusy || revizeTalimat.trim().length < 5) return;
    setRevizeBusy(true); setRevizeMesaj(null);
    try {
      const r = await fetch(`/api/domains/${activeDomainId}/page-strengthen/manuel-revize`, {
        method: "POST", headers: { "content-type": "application/json" },
        body: JSON.stringify({ pageId, talimat: revizeTalimat.trim() }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) { setRevizeMesaj(j.error ?? "Revize başarısız"); return; }
      setEdited(null); setEditMode(false); setRevizeTalimat("");
      setRevizeMesaj("✓ Revize uygulandı — değişen cümleler yeşil işaretli.");
      queryClient.invalidateQueries({ queryKey: ["page-strengthen", pageId] });
    } catch { setRevizeMesaj("Bağlantı hatası"); }
    finally { setRevizeBusy(false); }
  };
  const [zipBusy, setZipBusy] = useState(false);

  // İçerik üretim hattıyla AYNI paket: gerçek .docx (görseller gömülü) + gorseller/ + meta.txt
  const downloadZip = async (markdown: string, fname: string) => {
    if (!activeDomainId || !pageId || zipBusy) return;
    setZipBusy(true);
    try {
      const resp = await fetch(`/api/domains/${activeDomainId}/page-strengthen/export`, {
        method: "POST", headers: { "content-type": "application/json" },
        body: JSON.stringify({ pageId, markdown }),
      });
      if (!resp.ok) throw new Error("export");
      const blob = await resp.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob); a.download = `${fname}.zip`; a.click();
      URL.revokeObjectURL(a.href);
    } catch { alert("Dışa aktarma başarısız — sunucu loguna bakın."); }
    finally { setZipBusy(false); }
  };

  // "Güçlendirmeyi Başlat" veya sayfa detayındaki "Link Planını Güçlendirmede Uygula"
  // (?applyPlan=1) ile gelindiyse analizi otomatik başlat (tek sefer).
  const applyPlanTriggered = useRef(false);
  useEffect(() => {
    if (!pageId) return;
    const wantApply = new URLSearchParams(window.location.search).has("applyPlan");
    if ((sessionStorage.getItem("ps-autorun") === String(pageId) || (wantApply && !applyPlanTriggered.current)) && !runStr.isPending) {
      sessionStorage.removeItem("ps-autorun");
      if (wantApply) applyPlanTriggered.current = true;
      runStr.mutate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageId]);

  const { data: pagesList } = useQuery({
    queryKey: ["pages-list", activeDomainId],
    enabled: !!activeDomainId,
    queryFn: async (): Promise<{ pages: PageItem[] }> => {
      try { const r = await fetch(`/api/domains/${activeDomainId}/pages-list`); return r.ok ? await r.json() : { pages: [] }; }
      catch { return { pages: [] }; }
    },
  });
  const { data: str } = useQuery({
    queryKey: ["page-strengthen", pageId],
    enabled: !!pageId,
    // Analiz artık ARKA PLANDA koşar: sürerken 4 sn'de bir durum yoklanır, bitince sonuç kendiliğinden gelir
    refetchInterval: (q) => ((q.state.data as StrResp | undefined)?.running ? 4000 : false),
    queryFn: async (): Promise<StrResp> => {
      try { const r = await fetch(`/api/domains/${activeDomainId}/page-strengthen?pageId=${pageId}`); return r.ok ? await r.json() : { configured: false, data: null, runAt: null }; }
      catch { return { configured: false, data: null, runAt: null }; }
    },
  });
  const analizSuruyor = !!str?.running;
  // Koşu bitince liste görünümü de tazelensin (yeni kart/summary)
  const oncekiSuruyor = useRef(false);
  useEffect(() => {
    if (oncekiSuruyor.current && !analizSuruyor) {
      queryClient.invalidateQueries({ queryKey: ["strengthened-pages", activeDomainId] });
      setEdited(null); setEditMode(false);
    }
    oncekiSuruyor.current = analizSuruyor;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analizSuruyor]);
  // KELİME HEDEFİ (kullanıcı isteği: "çok uzun yazıyoruz, 500-700 civarı olmalı") —
  // varsayılan 600, seçim tarayıcıda hatırlanır; 0 = stratejist kendisi belirler.
  const [kelimeHedefi, setKelimeHedefi] = useState<number>(() => { const v = Number(localStorage.getItem("ps-kelime-hedefi")); return Number.isFinite(v) && v >= 0 && localStorage.getItem("ps-kelime-hedefi") !== null ? v : 600; });
  const kelimeHedefiSec = (v: number) => { setKelimeHedefi(v); localStorage.setItem("ps-kelime-hedefi", String(v)); };
  const runStr = useMutation({
    mutationFn: async (): Promise<StrResp> => {
      // ?applyPlan=1 ile gelindiyse sayfanın kayıtlı link inşa planı revizeye öncelikli enjekte edilir
      const applySavedLinkPlan = new URLSearchParams(window.location.search).has("applyPlan");
      // Ağ kesintisi (sunucu yeniden başladı / bağlantı koptu) jenerik "Analiz hatası"
      // yerine anlaşılır mesaj versin — analiz uzun sürer, kopma en çok burada görülür.
      let r: Response;
      try {
        r = await fetch(`/api/domains/${activeDomainId}/page-strengthen`, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ pageId, applySavedLinkPlan, kelimeHedefi }) });
      } catch {
        throw new Error("Sunucuya ulaşılamadı — bağlantı kopmuş ya da sunucu yeniden başlamış olabilir. Birkaç saniye bekleyip 'Güçlendir'e tekrar bas.");
      }
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error ?? `Analiz hatası (HTTP ${r.status}) — tekrar dene, sürerse logu kontrol edelim.`);
      return r.json();
    },
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["page-strengthen", pageId] }); },
  });

  // Güçlendirilen sayfalar — kart listesi (motor ana görünümü)
  type StrengthenedItem = { pageId: number; runAt: string; running?: boolean; url: string; title: string; summary: string; planCount: number; hasRevised: boolean; hasMetaBoost: boolean; thumb?: string | null };
  const { data: strengthened } = useQuery({
    queryKey: ["strengthened-pages", activeDomainId],
    enabled: !!activeDomainId && !pageId,
    // Süren analiz varsa liste kendini tazeler — kart "analiz sürüyor"dan sonuca döner
    refetchInterval: (q) => ((q.state.data as { items?: StrengthenedItem[] } | undefined)?.items?.some((i) => i.running) ? 8000 : false),
    queryFn: async (): Promise<{ items: StrengthenedItem[] }> => {
      try { const r = await fetch(`/api/domains/${activeDomainId}/strengthened-pages`); return r.ok ? await r.json() : { items: [] }; } catch { return { items: [] }; }
    },
  });

  return (
    <div id="ps-print-root" className="px-4 sm:px-6 py-6 w-full">
      {OnayPenceresi}
      {/* Yazdırma/PDF: sidebar+kontroller+içerik bölümü gizli; karanlık tema → okunabilir (koyu metin/beyaz zemin) */}
      <style>{`@media print {
        aside { display: none !important; }
        .ps-no-print, .ps-print-hide { display: none !important; }
        html, body { background: #fff !important; }
        #ps-print-root { max-width: none !important; padding: 0 !important; }
        #ps-print-root, #ps-print-root * {
          color: #111 !important;
          background-color: #fff !important;
          box-shadow: none !important;
          border-color: #cfcfcf !important;
        }
        #ps-print-root a { color: #0563c1 !important; }
        #ps-print-root .ps-card { break-inside: avoid; border: 1px solid #ddd !important; }
      }`}</style>
      {/* ── SAYFA GÜÇLENDİRME AJANI — ajan ailesi stili ── */}
      <div className="flex items-center justify-between gap-4 flex-wrap mb-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl flex items-center justify-center text-2xl shrink-0" style={{ background: "rgba(139,92,246,0.12)" }}>⚡</div>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold text-foreground">Sayfa Güçlendirme Ajanı</h1>
              <span className="text-[11px] px-2.5 py-1 rounded-lg font-medium" style={runStr.isPending ? { background: `${C.cyan}18`, color: "#b12220" } : { background: "rgba(34,197,94,0.1)", color: "#039855" }}>
                {runStr.isPending ? "⏳ Güçlendiriyor" : "● Hazır"}
              </span>
            </div>
            <p className="text-sm text-muted-foreground">Sayfanın tüm sinyallerini birleştirir; aksiyon planı, başlık+meta boost ve güçlendirilmiş içerik üretir.</p>
          </div>
        </div>
      </div>

      {/* LİSTE MODU: ajan paneli (solda sayfa seçimi, sağda güçlendirme kapsamı) + kart listesi */}
      {!pageId && (<>
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(300px,1fr)_minmax(280px,380px)] gap-4 mb-4 lg:items-start">
          {/* Sol: Güçlendirilecek Sayfa */}
          <Card><CardContent className="p-4">
            <div className="text-sm font-semibold mb-2">📄 Güçlendirilecek Sayfa</div>
            <div className="relative mb-2">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input value={agentSearch} onChange={(e) => setAgentSearch(e.target.value)} placeholder="Başlık veya URL ile ara…"
                className="w-full text-xs pl-8 pr-3 py-2.5 rounded-xl border outline-none bg-white border-black/10 text-gray-800 placeholder:text-gray-400 dark:bg-gray-800 dark:border-gray-700 dark:text-white/90 dark:placeholder:text-gray-500" />
            </div>
            <div className="max-h-[400px] overflow-y-auto space-y-2 pr-1">
              {(pagesList?.pages ?? [])
                .filter((p) => { const q = agentSearch.toLowerCase(); return !q || (p.title ?? "").toLowerCase().includes(q) || p.url.toLowerCase().includes(q); })
                .slice(0, 150)
                .map((p) => {
                  const active = selPid === p.id;
                  return (
                    <button key={p.id} onClick={() => setSelPid(p.id)} className={`w-full text-left px-3 py-2.5 rounded-xl transition border ${active ? "dark:bg-white/[0.04]" : "bg-white border-black/10 dark:bg-gray-800 dark:border-gray-700"}`}
                      style={active ? { background: "rgba(139,92,246,0.06)", borderColor: "#e83e3c", boxShadow: "0 0 0 1px #e83e3c inset" } : undefined}>
                      <div className="flex items-start gap-2">
                        <span className="mt-0.5 w-3.5 h-3.5 rounded-full border shrink-0 flex items-center justify-center" style={active ? { borderColor: "#e83e3c", background: "#e83e3c" } : { borderColor: "rgba(0,0,0,0.25)" }}>
                          {active && <span className="w-1.5 h-1.5 rounded-full bg-white" />}
                        </span>
                        <div className="min-w-0 flex-1">
                          <div className="text-xs font-semibold truncate" style={active ? { color: "#d42b29" } : undefined}>{p.title || shortUrl(p.url)}</div>
                          <div className="text-[10px] text-muted-foreground truncate">{shortUrl(p.url)}{p.primaryKeyword ? ` · 🎯 ${p.primaryKeyword}` : ""}</div>
                        </div>
                      </div>
                    </button>
                  );
                })}
              {(pagesList?.pages ?? []).length === 0 && <div className="text-xs text-muted-foreground p-2">Sayfa bulunamadı — önce siteyi tara.</div>}
            </div>
          </CardContent></Card>

          {/* Sağ: seçim özeti + başlat — içerik kadar yükseklik, boşluk bırakmaz */}
          <Card><CardContent className="p-5">
            <div className="text-sm font-semibold mb-3">🚀 Güçlendirme</div>
            {selPid ? (
              <div className="rounded-xl border p-3 mb-4" style={{ borderColor: "rgba(139,92,246,0.35)", background: "rgba(139,92,246,0.04)" }}>
                <div className="text-xs font-semibold text-brand-600 dark:text-brand-400">{(pagesList?.pages ?? []).find((p) => p.id === selPid)?.title || "Seçili sayfa"}</div>
                <div className="text-[11px] text-muted-foreground truncate">{shortUrl((pagesList?.pages ?? []).find((p) => p.id === selPid)?.url ?? "")}</div>
              </div>
            ) : (
              <p className="text-xs text-muted-foreground mb-4 leading-relaxed">Soldan güçlendirilecek sayfayı seç. Ajan tek koşuda arama sinyallerini, AI görünürlüğünü, rakip trafiğini ve linkleri birleştirip aksiyon planı + güçlendirilmiş içerik üretir.</p>
            )}
            <button disabled={!selPid} onClick={() => { if (selPid) { sessionStorage.setItem("ps-autorun", String(selPid)); setPageId(selPid); } }}
              className="w-full py-3 rounded-xl text-sm font-semibold text-white transition hover:opacity-90 disabled:opacity-50 inline-flex items-center justify-center gap-2"
              style={{ background: "linear-gradient(135deg, #d42b29, #e83e3c)" }}>
              ⚡ Güçlendirmeyi Başlat
            </button>
            <p className="text-[10px] text-muted-foreground mt-2 text-center">~750 token · 2-3 dk · sonuç detay sayfasında açılır</p>
          </CardContent></Card>
        </div>

        {(strengthened?.items?.length ?? 0) > 0 && <div className="text-sm font-semibold mb-2">Güçlendirilen Sayfalar</div>}
        {(strengthened?.items?.length ?? 0) === 0 && (
          <Card><CardContent className="p-10 text-center text-sm text-muted-foreground"><Zap className="w-10 h-10 mx-auto mb-3 opacity-40" />Henüz güçlendirilen sayfa yok. Yukarıdan bir sayfa seçip "Güçlendirmeyi Başlat"a bas.</CardContent></Card>
        )}
        <div className="space-y-3">
          {(strengthened?.items ?? []).map((it) => (
            <Link key={it.pageId} href={`/page-strengthen/${it.pageId}`}>
              <Card className="cursor-pointer transition hover:shadow-md mb-3"><CardContent className="p-4">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  {it.thumb && <img src={it.thumb} alt="" loading="lazy" onError={(e) => { e.currentTarget.style.display = "none"; }} className="rounded-lg object-cover shrink-0" style={{ width: 112, height: 70 }} />}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap mb-1 text-[11px]">
                      {it.running
                        ? <span className="px-2 py-0.5 rounded-full inline-flex items-center gap-1" style={{ background: "rgba(247,144,9,0.14)", color: "#b54708" }}><Loader2 className="w-3 h-3 animate-spin" /> analiz sürüyor</span>
                        : <span className="px-2 py-0.5 rounded-full" style={{ background: "rgba(34,197,94,0.12)", color: "#039855" }}>⚡ güçlendirildi</span>}
                      {it.hasRevised && <span className="px-2 py-0.5 rounded-full" style={{ background: "rgba(6,182,212,0.12)", color: "#b12220" }}>📝 revize içerik hazır</span>}
                      {it.hasMetaBoost && <span className="px-2 py-0.5 rounded-full" style={{ background: "rgba(59,130,246,0.1)", color: "#b12220" }}>✨ başlık+meta boost</span>}
                      <span className="text-muted-foreground">{it.planCount} aksiyon · {new Date(it.runAt).toLocaleDateString("tr-TR", { dateStyle: "medium" })}</span>
                    </div>
                    <div className="font-semibold text-sm">{it.title || shortUrl(it.url)}</div>
                    <div className="text-xs text-muted-foreground truncate">{shortUrl(it.url)}</div>
                    {it.summary && <p className="text-xs text-muted-foreground mt-1.5 line-clamp-2">{it.summary}</p>}
                  </div>
                  <span className="text-xs shrink-0 text-blue-light-700 dark:text-blue-light-500">Detay →</span>
                </div>
              </CardContent></Card>
            </Link>
          ))}
        </div>
      </>)}

      {/* DETAY MODU: geri + aksiyonlar */}
      {pageId && (
        <div className="flex items-center gap-3 flex-wrap mb-4 ps-no-print">
          <Link href="/page-strengthen"><Button size="sm" variant="ghost" className="gap-1.5">← Güçlendirilen Sayfalar</Button></Link>
          <label className="flex items-center gap-1.5 text-[12px] text-muted-foreground" title="Revize içeriğin hedef uzunluğu — kısa ve öz sayfalar için 500-700 önerilir">
            Kelime hedefi
            <select value={kelimeHedefi} onChange={(e) => kelimeHedefiSec(Number(e.target.value))} disabled={runStr.isPending || analizSuruyor}
              className="h-9 rounded-lg border border-gray-200 bg-transparent px-2 text-[12.5px] text-foreground outline-none dark:border-gray-800">
              {[[500, "500"], [600, "600 (önerilen)"], [700, "700"], [1000, "1000"], [1500, "1500"], [0, "Otomatik"]].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </label>
          <Button disabled={runStr.isPending || analizSuruyor} onClick={async () => { if (await confirmCost("strengthen")) runStr.mutate(); }} className="gap-1.5" style={{ background: C.green, color: "#141412" }}>
            {(runStr.isPending || analizSuruyor) ? <Loader2 className="w-4 h-4 animate-spin" /> : str?.data ? <RefreshCw className="w-4 h-4" /> : <Zap className="w-4 h-4" />}
            {(runStr.isPending || analizSuruyor) ? "Analiz ediliyor…" : str?.data ? "Yeniden Analiz (~750 token)" : "Güçlendir (~750 token)"}
          </Button>
          {str?.data && <Button variant="outline" className="gap-1.5" onClick={() => void pdfIndir("#ps-print-root", "sayfa-guclendirme")}>🖨️ PDF olarak indir</Button>}
          {/* WP gönderimi üst eylem satırında da dursun — kartın içinde gömülü kalınca bulunamıyordu */}
          {str?.data?.revisedContent && wpDurum.data?.bagli && (
            <Button disabled={wpBusy} className="gap-1.5 bg-brand-500 hover:bg-brand-600" title="Başlık + içerik + SEO meta WordPress'e yazılır; önce canlı içerik yedeklenir" onClick={() => void wpUygula()}>
              {wpBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : null} WordPress'e Gönder
            </Button>
          )}
          {str?.data?.revisedContent && wpDurum.data?.bagli && sonWpUygulama && (
            <Button variant="outline" disabled={wpBusy} className="gap-1.5 text-amber-600 border-amber-500/40" title="Uygulamadan önceki yedeğe geri döndür" onClick={() => void wpGeriAl()}>↩ Geri Al</Button>
          )}
        </div>
      )}

      {runStr.isError && <Card style={{ borderColor: "#f0443866" }}><CardContent className="p-3 text-sm text-red-400">{(runStr.error as Error)?.message}</CardContent></Card>}
      {str?.runError && !analizSuruyor && <Card style={{ borderColor: "#f0443866" }}><CardContent className="p-3 text-sm text-red-400">Son analiz hatayla bitti: {str.runError} — "Yeniden Analiz" ile tekrar dene.</CardContent></Card>}
      {analizSuruyor && (
        <Card className="mb-4" style={{ borderColor: "rgba(34,197,94,0.4)", background: "rgba(34,197,94,0.05)" }}><CardContent className="p-4">
          <div className="flex items-center gap-2.5 text-sm font-medium"><Loader2 className="w-4 h-4 animate-spin" style={{ color: C.green }} /> Analiz arka planda sürüyor (2-3 dk)</div>
          <p className="mt-1 text-[12px] text-muted-foreground">Arama verisi + sonuç sayfaları + rakip trafiği + AI motorları toplanıp birleştiriliyor. Bu sayfadan ayrılabilir, hatta sekmeyi kapatabilirsin — analiz sunucuda tamamlanır ve bittiğinde burada belirir.</p>
        </CardContent></Card>
      )}
      {runStr.isPending && !analizSuruyor && <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground py-8"><Loader2 className="w-5 h-5 animate-spin" /> Analiz başlatılıyor…</div>}

      {!runStr.isPending && pageId && !str?.data && (
        <Card><CardContent className="p-10 text-center text-sm text-muted-foreground">Bu sayfa için henüz analiz yok. “Güçlendir”e bas.</CardContent></Card>
      )}

      {!runStr.isPending && pageId && str?.data && (() => {
        const d = str.data;
        const order: Record<string, number> = { high: 0, medium: 1, low: 2 };
        const plan = [...(d.plan ?? [])].sort((a, b) => (order[a.priority] ?? 1) - (order[b.priority] ?? 1));
        const gaps = [...(d.contentGaps ?? [])].sort((a, b) => Number(a.covered) - Number(b.covered) || b.volume - a.volume);
        const aiov = d.aiOverview ?? [], comps = d.competitors ?? [], links = d.linkOpportunities ?? [], canni = d.cannibalization ?? [];
        const outb = d.outboundLinks ?? [], exIn = d.existingLinks?.incoming ?? [], exOut = d.existingLinks?.outgoing ?? [], sec = d.secondaryKeywords ?? [];
        const paa = d.paa ?? [], related = d.relatedSearches ?? [], aieng = d.aiEngines ?? [], aiAns = d.aiOverviewAnswer ?? { answer: "", sources: [], weAreCited: false }, cl = d.cluster ?? null;
        const kwComp = d.keywordCompetitors ?? [], warns = d.warnings ?? [];
        const sevColor = (s: string) => (s === "critical" ? C.red : s === "low" ? "#a3a3a0" : C.amber);
        const sevLabel = (s: string) => (s === "critical" ? "Kritik" : s === "low" ? "Düşük" : "Orta");
        return (
          <>
            {d.summary && (
              <Card className="mb-4" style={{ borderColor: "#12b76a55", background: "rgba(34,197,94,0.05)" }}><CardContent className="p-4">
                <div className="flex items-center gap-2 mb-1.5"><Zap className="w-4 h-4" style={{ color: C.green }} /><b className="text-sm">{shortUrl(d.pageUrl)}</b></div>
                <p className="text-sm text-muted-foreground">{d.summary}</p>
              </CardContent></Card>
            )}

            {/* Başlık + Meta Açıklama Boost */}
            {d.metaBoost && (
              <Card className="mb-4" style={{ borderColor: "rgba(66,133,244,0.35)" }}><CardContent className="p-4">
                <div className="flex items-center gap-2 mb-3"><Search className="w-4 h-4" style={{ color: C.blue }} /><span className="font-semibold text-sm">Başlık &amp; Meta Açıklama Boost</span><span className="text-[11px] text-muted-foreground">— arama sorguları + rakip başlıklarına göre optimize</span></div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div className="space-y-3">
                    <div>
                      <div className="text-[11px] font-medium text-muted-foreground mb-1">📄 Mevcut Başlık <span className="opacity-60">({(d.pageTitle || "").length} kr)</span></div>
                      <div className="text-sm p-2 rounded border" style={{ borderColor: "rgba(0,0,0,0.08)" }}>{d.pageTitle || "(yok)"}</div>
                    </div>
                    <div>
                      <div className="text-[11px] font-medium text-muted-foreground mb-1">📄 Mevcut Meta Açıklama <span className="opacity-60">({(d.metaDescription || "").length} kr)</span></div>
                      <div className="text-xs p-2 rounded border text-muted-foreground" style={{ borderColor: "rgba(0,0,0,0.08)" }}>{d.metaDescription || "(yok)"}</div>
                    </div>
                  </div>
                  <div className="space-y-3">
                    {(() => {
                      const tGoster = mbTitle ?? d.metaBoost.suggestedTitle;
                      const dGoster = mbDesc ?? d.metaBoost.suggestedDescription;
                      const degisti = tGoster !== d.metaBoost.suggestedTitle || dGoster !== d.metaBoost.suggestedDescription;
                      return (<>
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <div className="text-[11px] font-medium" style={{ color: C.green }}>✨ Önerilen Başlık <span className="opacity-60">({tGoster.length} kr{tGoster.length > 60 ? " ⚠️" : ""}) — düzenlenebilir</span></div>
                        <Button size="sm" variant="ghost" className="h-6 px-2 text-[11px]" onClick={() => { navigator.clipboard?.writeText(tGoster); setCopied("title"); setTimeout(() => setCopied(""), 1500); }}>{copied === "title" ? "✓" : "Kopyala"}</Button>
                      </div>
                      <input value={tGoster} onChange={(e) => setMbTitle(e.target.value)}
                        className="w-full text-sm p-2 rounded border font-medium bg-transparent outline-none focus:ring-2 focus:ring-emerald-500/30"
                        style={{ borderColor: "rgba(34,197,94,0.35)", background: "rgba(34,197,94,0.04)" }} />
                    </div>
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <div className="text-[11px] font-medium" style={{ color: C.green }}>✨ Önerilen Meta Açıklama <span className="opacity-60">({dGoster.length} kr{dGoster.length > 155 ? " ⚠️" : ""}) — düzenlenebilir</span></div>
                        <Button size="sm" variant="ghost" className="h-6 px-2 text-[11px]" onClick={() => { navigator.clipboard?.writeText(dGoster); setCopied("desc"); setTimeout(() => setCopied(""), 1500); }}>{copied === "desc" ? "✓" : "Kopyala"}</Button>
                      </div>
                      <textarea value={dGoster} onChange={(e) => setMbDesc(e.target.value)} rows={3}
                        className="w-full text-xs p-2 rounded border bg-transparent outline-none focus:ring-2 focus:ring-emerald-500/30"
                        style={{ borderColor: "rgba(34,197,94,0.35)", background: "rgba(34,197,94,0.04)" }} />
                    </div>
                    {degisti && (
                      <div className="flex items-center gap-2">
                        <Button size="sm" className="h-7 text-xs gap-1 bg-brand-500 hover:bg-brand-600" disabled={mbBusy}
                          onClick={() => void mbKaydet(d.metaBoost!.suggestedTitle, d.metaBoost!.suggestedDescription)}>
                          {mbBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null} Kaydet
                        </Button>
                        <Button size="sm" variant="outline" className="h-7 text-xs" disabled={mbBusy} onClick={() => { setMbTitle(null); setMbDesc(null); }}>Vazgeç</Button>
                        <span className="text-[11px] text-muted-foreground">Kaydedince "Siteye Uygula" bu hâli kullanır.</span>
                      </div>
                    )}
                      </>);
                    })()}
                  </div>
                </div>
                {d.metaBoost.rationale && <p className="text-[11px] text-muted-foreground mt-3">💡 {d.metaBoost.rationale}</p>}
              </CardContent></Card>
            )}

            {/* GSC metrik kartları */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
              {[
                { label: "Tıklama", value: fmt(d.gsc?.clicks ?? 0), icon: MousePointerClick, color: C.green },
                { label: "Gösterim", value: fmt(d.gsc?.impressions ?? 0), icon: Eye, color: C.blue },
                { label: "CTR", value: `%${((d.gsc?.ctr ?? 0) * 100).toFixed(1)}`, icon: Percent, color: C.amber },
                { label: "Ort. Pozisyon", value: (d.gsc?.position ?? 0) > 0 ? (d.gsc.position).toFixed(1) : "—", icon: ArrowDownUp, color: C.purple },
              ].map((s) => (
                <Card key={s.label}><CardContent className="p-3">
                  <div className="flex items-center justify-between"><span className="text-[11px] text-muted-foreground">{s.label}</span><s.icon className="w-3.5 h-3.5" style={{ color: s.color }} /></div>
                  <div className="text-xl font-bold mt-1 tabular-nums">{s.value}</div>
                </CardContent></Card>
              ))}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
              {[
                { label: "Ana Kelime Hacmi", value: d.keywordVolume ? `${fmt(d.keywordVolume)}/ay` : "—", icon: Search, color: C.amber },
                { label: "AI Overview", value: `${aiov.length} sıralama`, icon: Sparkles, color: C.purple },
                { label: "Organik Rakip", value: String(comps.length), icon: Trophy, color: C.red },
                { label: "İç Link", value: `${d.internalLinks?.incoming ?? 0} gelen / ${d.internalLinks?.outgoing ?? 0} giden`, icon: Link2, color: C.cyan },
              ].map((s) => (
                <Card key={s.label}><CardContent className="p-3">
                  <div className="flex items-center justify-between"><span className="text-[11px] text-muted-foreground">{s.label}</span><s.icon className="w-3.5 h-3.5" style={{ color: s.color }} /></div>
                  <div className="text-sm font-semibold mt-1 tabular-nums">{s.value}</div>
                </CardContent></Card>
              ))}
            </div>

            {/* Sayfaya özel CANLI GSC — 24s-16ay aralık + metrik grafiği + filtreli sorgu tablosu (trafiğin hemen altında) */}
            <PageGscPanel domainId={activeDomainId} pageId={pageId} />

            {aieng.length > 0 && (
              <Card className="mb-4" style={{ borderColor: "rgba(139,92,246,0.3)" }}><CardContent className="p-4">
                <div className="flex items-center gap-2 mb-3"><Sparkles className="w-4 h-4" style={{ color: C.purple }} /><span className="font-semibold text-sm">AI Motorlarında Görünürlük</span><span className="text-[11px] text-muted-foreground">(bu konuda kaynak gösteriliyor muyuz)</span></div>
                <div className="flex flex-wrap gap-2">
                  {aieng.map((e, i) => (
                    <span key={i} className="text-xs px-3 py-1.5 rounded-lg inline-flex items-center gap-1.5" style={{ background: e.appears ? `${C.green}18` : "rgba(0,0,0,0.04)", color: e.appears ? C.green : "#a3a3a0", border: `1px solid ${e.appears ? C.green + "55" : "rgba(0,0,0,0.08)"}` }}>
                      {e.appears ? <Check className="w-3.5 h-3.5" /> : <span>✗</span>} {e.engine}
                    </span>
                  ))}
                </div>
              </CardContent></Card>
            )}

            {/* Ana kelimede rakip trafiği */}
            {kwComp.length > 0 && (
              <Card className="mb-4" style={{ borderColor: "rgba(239,68,68,0.25)" }}><CardContent className="p-4">
                <div className="flex items-center gap-2 mb-1"><Trophy className="w-4 h-4" style={{ color: C.red }} /><span className="font-semibold text-sm">“{d.primaryKeyword || d.pageTitle}” — Rakip Trafiği</span></div>
                <p className="text-[11px] text-muted-foreground mb-3">Bu kelimede Google'da sıralanan siteler ve bu kelimeden tahmini aylık trafikleri (aylık hacim {fmt(d.keywordVolume)} × pozisyon CTR'si)</p>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm min-w-[480px]">
                    <thead><tr className="text-[11px] text-muted-foreground text-left border-b" style={{ borderColor: "rgba(0,0,0,0.08)" }}>
                      <th className="py-1.5 w-10">#</th><th className="py-1.5">Site</th><th className="py-1.5">Başlık</th><th className="py-1.5 text-right">Tahmini Trafik/ay</th>
                    </tr></thead>
                    <tbody>{kwComp.map((c, i) => (
                      <tr key={i} className="border-b last:border-0" style={{ borderColor: "rgba(0,0,0,0.05)", background: c.isUs ? "rgba(34,197,94,0.08)" : undefined }}>
                        <td className="py-1.5 tabular-nums font-semibold" style={{ color: c.position <= 3 ? C.green : c.position <= 10 ? C.amber : "#a3a3a0" }}>{c.position}</td>
                        <td className="py-1.5 pr-3">{c.domain}{c.isUs && <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded" style={{ background: `${C.green}22`, color: C.green }}>BİZ</span>}</td>
                        <td className="py-1.5 pr-3 text-muted-foreground truncate max-w-[280px]" title={c.title}>{c.title}</td>
                        <td className="py-1.5 text-right tabular-nums font-medium" style={{ color: c.isUs ? C.green : undefined }}>{fmt(c.estimatedTraffic)}</td>
                      </tr>
                    ))}</tbody>
                  </table>
                </div>
              </CardContent></Card>
            )}

            {/* Detaylı rekabet & AI analizi */}
            {d.competitiveAnalysis && (
              <Card className="mb-4"><CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2"><Sparkles className="w-4 h-4" style={{ color: C.purple }} /><span className="font-semibold text-sm">Rekabet & AI Manzarası Analizi</span></div>
                <div className="text-sm text-muted-foreground"><MiniMarkdown text={d.competitiveAnalysis} /></div>
              </CardContent></Card>
            )}

            {aiAns.answer && (
              <Card className="mb-4"><CardContent className="p-4">
                <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
                  <div className="flex items-center gap-2"><Sparkles className="w-4 h-4" style={{ color: C.purple }} /><span className="font-semibold text-sm">Google AI Overview — Şu Anki Cevap</span></div>
                  <span className="text-[11px] px-2 py-0.5 rounded-full" style={{ background: aiAns.weAreCited ? `${C.green}22` : `${C.red}18`, color: aiAns.weAreCited ? C.green : C.red }}>{aiAns.weAreCited ? "✓ Biz alıntılanıyoruz" : "✗ Biz alıntılanmıyoruz"}</span>
                </div>
                <p className="text-xs text-muted-foreground leading-relaxed max-h-40 overflow-y-auto whitespace-pre-wrap">{aiAns.answer}</p>
                {aiAns.sources.length > 0 && <div className="text-[11px] text-muted-foreground mt-2">Alıntılanan kaynaklar: {aiAns.sources.map((x) => x.domain).join(", ")}</div>}
              </CardContent></Card>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {/* Plan */}
              <div className="lg:col-span-2 space-y-4">
                <Card><CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-3"><Zap className="w-4 h-4" style={{ color: C.green }} /><span className="font-semibold text-sm">Güçlendirme Planı (öncelikli)</span></div>
                  <div className="space-y-2.5">{plan.map((p, i) => {
                    const pc = p.priority === "high" ? C.red : p.priority === "low" ? "#a3a3a0" : C.amber;
                    return (
                      <div key={i} className="flex gap-2.5 p-2.5 rounded-lg" style={{ background: "rgba(0,0,0,0.02)" }}>
                        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded shrink-0 h-fit" style={{ background: `${pc}22`, color: pc }}>{p.priority === "high" ? "YÜKSEK" : p.priority === "low" ? "DÜŞÜK" : "ORTA"}</span>
                        <div className="min-w-0">
                          <span className="text-[10px] px-1.5 py-0.5 rounded mr-1.5" style={{ background: "rgba(0,0,0,0.06)", color: "#a3a3a0" }}>{p.category}</span>
                          <span className="text-sm leading-relaxed">{p.action}</span>
                        </div>
                      </div>
                    );
                  })}</div>
                </CardContent></Card>

                {/* Linkleme uyarıları */}
                {warns.length > 0 && (
                  <Card style={{ borderColor: "rgba(245,158,11,0.3)" }}><CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-3"><AlertTriangle className="w-4 h-4" style={{ color: C.amber }} /><span className="font-semibold text-sm">Linkleme Uyarıları</span><span className="text-[11px] text-muted-foreground">({warns.length})</span></div>
                    <div className="space-y-2.5">{warns.map((w, i) => (
                      <div key={i} className="p-2.5 rounded-lg" style={{ background: "rgba(0,0,0,0.02)", borderLeft: `2px solid ${sevColor(w.severity)}` }}>
                        <div className="flex items-center gap-2 flex-wrap mb-0.5">
                          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded" style={{ background: `${sevColor(w.severity)}22`, color: sevColor(w.severity) }}>{sevLabel(w.severity)}</span>
                          <span className="text-sm font-medium">{w.label}</span>
                          {w.anchor && <span className="text-[11px] text-muted-foreground">anchor: <span style={{ color: C.cyan }}>“{w.anchor}”</span></span>}
                        </div>
                        <p className="text-xs text-muted-foreground">{w.message}</p>
                        {w.targetUrl && <div className="text-[11px] text-muted-foreground mt-0.5 truncate">→ {shortUrl(w.targetUrl)}</div>}
                        {w.suggested && <div className="text-[11px] mt-1 p-1.5 rounded" style={{ background: "rgba(34,197,94,0.06)", color: "#039855" }}>Öneri: {w.suggested}</div>}
                      </div>
                    ))}</div>
                  </CardContent></Card>
                )}

                {/* Mevcut iç linkler — gelen + giden detaylı */}
                {(exIn.length > 0 || exOut.length > 0) && (
                  <Card><CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-3"><Link2 className="w-4 h-4" style={{ color: C.cyan }} /><span className="font-semibold text-sm">Mevcut İç Linkler</span></div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <div className="text-[11px] font-medium text-muted-foreground mb-1.5">← Gelen linkler ({d.internalLinks.incoming}) — hangi sayfalardan</div>
                        <div className="space-y-1">{exIn.length ? exIn.slice(0, 20).map((l, i) => (
                          <div key={i} className="text-xs truncate"><a href={l.url} target="_blank" rel="noopener noreferrer" className="text-muted-foreground underline decoration-dotted hover:text-cyan-400">{shortUrl(l.url)}</a> · <span style={{ color: C.cyan }}>“{l.anchor || "(link)"}”</span></div>
                        )) : <div className="text-xs text-muted-foreground">gelen iç link yok (orphan riski)</div>}</div>
                      </div>
                      <div>
                        <div className="text-[11px] font-medium text-muted-foreground mb-1.5">→ Giden linkler ({d.internalLinks.outgoing})</div>
                        <div className="space-y-1">{exOut.length ? exOut.slice(0, 20).map((l, i) => (
                          <div key={i} className="text-xs truncate"><span style={{ color: C.cyan }}>“{l.anchor || "(link)"}”</span> · <a href={l.url} target="_blank" rel="noopener noreferrer" className="text-muted-foreground underline decoration-dotted hover:text-cyan-400">{shortUrl(l.url)}</a></div>
                        )) : <div className="text-xs text-muted-foreground">giden iç link yok</div>}</div>
                      </div>
                    </div>
                  </CardContent></Card>
                )}
              </div>

              {/* Sinyaller */}
              <div className="space-y-4">
                {cl && (
                  <Card><CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-2"><Trophy className="w-4 h-4" style={{ color: C.blue }} /><span className="font-semibold text-sm">Cluster Bağlamı</span></div>
                    <div className="text-xs"><b>{cl.name}</b>{cl.pillarUrl && <div className="text-[11px] text-muted-foreground mt-0.5">pillar: <a href={cl.pillarUrl} target="_blank" rel="noopener noreferrer" style={{ color: C.cyan }} className="underline decoration-dotted">{shortUrl(cl.pillarUrl)}</a></div>}</div>
                    {cl.siblings.length > 0 && (
                      <div className="mt-2 pt-2 border-t" style={{ borderColor: "rgba(0,0,0,0.06)" }}>
                        <div className="text-[11px] text-muted-foreground mb-1">{cl.siblings.length} kardeş sayfa (iç link için):</div>
                        <div className="space-y-1 max-h-44 overflow-y-auto">{cl.siblings.map((s, i) => (
                          <a key={i} href={s.url} target="_blank" rel="noopener noreferrer" className="block text-xs truncate hover:bg-black/[0.04] rounded px-1 py-0.5" title={s.title || s.url}>
                            <span style={{ color: C.cyan }} className="underline decoration-dotted">{shortUrl(s.url)}</span>{s.title && <span className="text-muted-foreground"> · {s.title}</span>}
                          </a>
                        ))}</div>
                      </div>
                    )}
                  </CardContent></Card>
                )}

                {paa.length > 0 && (
                  <Card><CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-2.5"><Search className="w-4 h-4" style={{ color: C.green }} /><span className="font-semibold text-sm">İlişkili Sorular (PAA)</span></div>
                    <div className="space-y-1.5">{paa.map((q, i) => <div key={i} className="text-xs text-muted-foreground">? {q}</div>)}</div>
                    {related.length > 0 && <div className="text-[11px] text-muted-foreground mt-2 pt-2 border-t" style={{ borderColor: "rgba(0,0,0,0.06)" }}>İlişkili aramalar: {related.slice(0, 6).join(" · ")}</div>}
                  </CardContent></Card>
                )}

                <Card><CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-2.5"><Search className="w-4 h-4" style={{ color: C.amber }} /><span className="font-semibold text-sm">İçerik Boşluğu (alt başlıklar)</span></div>
                  <div className="space-y-1.5">{gaps.slice(0, 12).map((g) => (
                    <div key={g.keyword} className="flex items-center justify-between gap-2 text-xs">
                      <span className="truncate flex-1 min-w-0 flex items-center gap-1.5">{g.covered ? <Check className="w-3 h-3 shrink-0" style={{ color: C.green }} /> : <span className="text-red-400 shrink-0">✗</span>}<span className={g.covered ? "text-muted-foreground" : ""}>{g.keyword}</span></span>
                      <span className="text-muted-foreground tabular-nums shrink-0">{fmt(g.volume)}/ay</span>
                    </div>
                  ))}</div>
                </CardContent></Card>

                <Card><CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-2.5"><Trophy className="w-4 h-4" style={{ color: C.red }} /><span className="font-semibold text-sm">Organik Rakipler (domain)</span></div>
                  <div className="space-y-1.5">{comps.slice(0, 8).map((c) => (
                    <div key={c.domain} className="flex items-center justify-between gap-2 text-xs">
                      <span className="truncate flex-1 min-w-0">{c.domain}</span>
                      <span className="text-muted-foreground tabular-nums shrink-0">{c.commonKeywords} kw · poz {c.avgPosition}</span>
                    </div>
                  ))}</div>
                </CardContent></Card>

                <Card><CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-2.5"><Target className="w-4 h-4" style={{ color: C.amber }} /><span className="font-semibold text-sm">Anahtar Kelimeler</span></div>
                  <div className="flex flex-wrap gap-1.5">
                    {d.primaryKeyword && <span className="text-[11px] px-2 py-0.5 rounded-full" style={{ background: `${C.amber}22`, color: C.amber }}>★ {d.primaryKeyword}</span>}
                    {sec.map((kw, i) => <span key={i} className="text-[11px] px-2 py-0.5 rounded-full" style={{ background: "rgba(0,0,0,0.05)" }}>{kw}</span>)}
                    {!d.primaryKeyword && sec.length === 0 && <span className="text-xs text-muted-foreground">—</span>}
                  </div>
                </CardContent></Card>

                {outb.length > 0 && (
                  <Card style={{ borderColor: `${C.green}33` }}><CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-2.5"><Link2 className="w-4 h-4" style={{ color: C.green }} /><span className="font-semibold text-sm">İç Link Planı <span className="text-[11px] text-muted-foreground font-normal">(revize içeriğe işlendi)</span></span></div>
                    <div className="space-y-2.5">{outb.map((l, i) => (
                      <div key={i} className="text-xs pb-2 last:pb-0 border-b last:border-0" style={{ borderColor: "rgba(0,0,0,0.06)" }}>
                        <div className="truncate"><span style={{ color: C.green }}>“{l.anchor}”</span> → <a href={l.url} target="_blank" rel="noopener noreferrer" className="underline decoration-dotted hover:text-cyan-400">{shortUrl(l.url)}</a></div>
                        {l.reason && <div className="text-[11px] text-muted-foreground mt-1 leading-relaxed break-words">{l.reason}</div>}
                      </div>
                    ))}</div>
                  </CardContent></Card>
                )}

                {d.aiSuggestion && (
                  <Card><CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-2"><Sparkles className="w-4 h-4" style={{ color: C.purple }} /><span className="font-semibold text-sm">Mevcut AI Önerisi (link analizi)</span></div>
                    <p className="text-xs text-muted-foreground whitespace-pre-wrap max-h-44 overflow-y-auto">{d.aiSuggestion}</p>
                  </CardContent></Card>
                )}

                {links.length > 0 && (
                  <Card><CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-2.5"><Link2 className="w-4 h-4" style={{ color: C.cyan }} /><span className="font-semibold text-sm">Link Building Fırsatları (gelen)</span></div>
                    <div className="space-y-2">{links.map((l, i) => (
                      <div key={i} className="text-xs">
                        <div className="truncate" title={l.fromUrl}><a href={l.fromUrl} target="_blank" rel="noopener noreferrer" style={{ color: C.cyan }} className="underline decoration-dotted">{shortUrl(l.fromUrl)}</a> <span className="text-muted-foreground">· {l.reason}</span></div>
                        <div className="text-[11px] text-muted-foreground">anchor: <span style={{ color: C.cyan }}>“{l.anchor}”</span></div>
                      </div>
                    ))}</div>
                  </CardContent></Card>
                )}

                {canni.length > 0 && (
                  <Card style={{ borderColor: "rgba(239,68,68,0.3)" }}><CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-2.5"><span className="text-red-400 text-base leading-none">⚠</span><span className="font-semibold text-sm">Kanibalizasyon</span></div>
                    <div className="space-y-2">{canni.map((c, i) => (
                      <div key={i} className="text-xs">
                        <div className="font-medium truncate">“{c.query}”</div>
                        {(c.pages ?? []).map((p, j) => <div key={j} className="text-[11px] text-muted-foreground truncate">· {shortUrl(p.url)} <span className="tabular-nums">(poz {(p.position ?? 0).toFixed(1)})</span></div>)}
                      </div>
                    ))}</div>
                  </CardContent></Card>
                )}

                {aiov.length > 0 && (
                  <Card><CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-2.5"><Sparkles className="w-4 h-4" style={{ color: C.purple }} /><span className="font-semibold text-sm">AI Overview Sıralamaları</span></div>
                    <div className="space-y-1.5">{aiov.slice(0, 8).map((a, i) => (
                      <div key={i} className="flex items-center justify-between gap-2 text-xs">
                        <span className="truncate flex-1 min-w-0">{a.question}</span>
                        <span className="tabular-nums shrink-0" style={{ color: (a.position ?? 99) <= 3 ? C.green : C.amber }}>#{a.position}/{a.totalSources}</span>
                      </div>
                    ))}</div>
                  </CardContent></Card>
                )}
              </div>
            </div>

            {/* İçerik Brief'i — stratejistin (Aşama 1) içerik yazarına verdiği plan; içerik bundan yazıldı */}
            {d.brief && (
              <Card className="mt-4" style={{ borderColor: "rgba(139,92,246,0.3)" }}><CardContent className="p-4">
                <div className="flex items-center gap-2 mb-1"><FileText className="w-4 h-4" style={{ color: C.purple }} /><span className="font-semibold text-sm">İçerik Brief'i</span><span className="text-[11px] text-muted-foreground">— stratejist planı (içerik bundan üretildi) · hedef ~{d.brief.wordTarget} kelime</span></div>
                {d.brief.intent && <p className="text-xs text-muted-foreground mb-3">🎯 Arama niyeti: {d.brief.intent}</p>}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div>
                    <div className="text-[11px] font-semibold text-muted-foreground mb-1.5">📑 Başlık Planı (Outline)</div>
                    <div className="space-y-1">{d.brief.outline.map((o, i) => (
                      <div key={i} className="text-xs">
                        <div className="font-medium">{o.h2}</div>
                        {o.h3.length > 0 && <div className="pl-3 text-muted-foreground">{o.h3.map((h, j) => <div key={j}>– {h}</div>)}</div>}
                      </div>
                    ))}{!d.brief.outline.length && <span className="text-xs text-muted-foreground">—</span>}</div>
                  </div>
                  <div className="space-y-3">
                    {d.brief.faq.length > 0 && <div>
                      <div className="text-[11px] font-semibold text-muted-foreground mb-1.5">❓ FAQ</div>
                      <div className="space-y-0.5">{d.brief.faq.map((q, i) => <div key={i} className="text-xs text-muted-foreground">? {q}</div>)}</div>
                    </div>}
                    {d.brief.internalLinks.length > 0 && <div>
                      <div className="text-[11px] font-semibold text-muted-foreground mb-1.5">🔗 İç Link Haritası ({d.brief.internalLinks.length} · her URL tek)</div>
                      <div className="space-y-0.5">{d.brief.internalLinks.map((l, i) => (
                        <div key={i} className="text-xs truncate"><span style={{ color: C.green }}>“{l.anchor}”</span> → <a href={l.url} target="_blank" rel="noopener noreferrer" className="underline decoration-dotted hover:text-cyan-400 text-muted-foreground">{shortUrl(l.url)}</a></div>
                      ))}</div>
                    </div>}
                    {d.brief.mustInclude.length > 0 && <div>
                      <div className="text-[11px] font-semibold text-muted-foreground mb-1.5">✓ Mutlaka Geçecek (uydurma engeli)</div>
                      <div className="flex flex-wrap gap-1">{d.brief.mustInclude.map((m, i) => <span key={i} className="text-[10px] px-1.5 py-0.5 rounded" style={{ background: "rgba(0,0,0,0.05)" }}>{m}</span>)}</div>
                    </div>}
                    {d.brief.trustSignalIdeas?.length > 0 && <div>
                      <div className="text-[11px] font-semibold mb-1.5" style={{ color: C.amber }}>⚠️ Güven Sinyali Fikirleri (doğrula/doldur — içerikte yer tutucu olarak eklendi)</div>
                      <div className="space-y-0.5">{d.brief.trustSignalIdeas.map((t, i) => <div key={i} className="text-xs text-muted-foreground">• {t}</div>)}</div>
                    </div>}
                    {d.brief.competitorAngles.length > 0 && <div>
                      <div className="text-[11px] font-semibold text-muted-foreground mb-1.5">⚔️ Rakipten Öne Çıkma Açıları</div>
                      <div className="space-y-0.5">{d.brief.competitorAngles.map((a, i) => <div key={i} className="text-xs text-muted-foreground">• {a}</div>)}</div>
                    </div>}
                  </div>
                </div>
              </CardContent></Card>
            )}

            {/* KALİTE DENETÇİSİ raporu — yazar çıktısı otomatik denetlendi */}
            {d.auditReport && (
              <Card className="mt-4" style={{ borderColor: d.auditReport.passed ? "rgba(34,197,94,0.35)" : "rgba(245,158,11,0.4)" }}><CardContent className="p-4">
                <div className="flex items-center justify-between gap-2 flex-wrap mb-1">
                  <div className="flex items-center gap-2">
                    <span className="text-base leading-none">{d.auditReport.passed ? "🛡️" : "⚠️"}</span>
                    <span className="font-semibold text-sm">Kalite Denetçisi</span>
                    <span className="text-[11px] px-2 py-0.5 rounded-full font-bold tabular-nums" style={{ background: d.auditReport.score >= 80 ? `${C.green}22` : d.auditReport.score >= 60 ? `${C.amber}22` : `${C.red}22`, color: d.auditReport.score >= 80 ? C.green : d.auditReport.score >= 60 ? C.amber : C.red }}>{d.auditReport.score}/100</span>
                    {d.auditReport.fixApplied && <span className="text-[11px] px-2 py-0.5 rounded-full" style={{ background: `${C.cyan}18`, color: C.cyan }}>🔧 Kritik sorunlar otomatik düzeltildi</span>}
                  </div>
                  <span className="text-[11px] text-muted-foreground">{d.auditReport.issues.length === 0 ? "Sorun bulunamadı" : `${d.auditReport.issues.length} bulgu`}</span>
                </div>
                {d.auditReport.issues.length > 0 && (
                  <div className="space-y-1.5 mt-2">{d.auditReport.issues.map((iss, i) => (
                    <div key={i} className="text-xs flex gap-2">
                      <span className="text-[10px] font-bold px-1.5 py-0.5 rounded shrink-0 h-fit" style={{ background: iss.severity === "critical" ? `${C.red}22` : `${C.amber}18`, color: iss.severity === "critical" ? C.red : C.amber }}>{iss.severity === "critical" ? "KRİTİK" : "küçük"}</span>
                      <span className="text-muted-foreground"><b className="text-foreground/80">[{iss.type}]</b> {iss.message}</span>
                    </div>
                  ))}</div>
                )}
              </CardContent></Card>
            )}

            {/* Mevcut vs Revize İçerik (yan yana, görsel parite) */}
            {(() => {
              const revisedShown = edited ?? d.revisedContent ?? "";
              const fname = (shortUrl(d.pageUrl).replace(/[^a-z0-9]+/gi, "-").replace(/^-|-$/g, "") || "revize") + "-revize";
              return (
            <Card className="mt-4 ps-print-hide"><CardContent className="p-4">
              <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
                <div className="flex items-center gap-2"><FileText className="w-4 h-4" style={{ color: C.green }} /><span className="font-semibold text-sm">Mevcut vs Revize İçerik</span><span className="text-[11px] text-muted-foreground">— <mark style={{ background: "rgba(34,197,94,0.22)", color: "#166534", borderRadius: 3, padding: "0 3px" }}>yeşil = yeni/değişen</mark></span></div>
                {d.revisedContent && (
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <Button size="sm" variant={editMode ? "default" : "outline"} className="gap-1.5" onClick={() => { if (!editMode && edited === null) setEdited(d.revisedContent); setEditMode((v) => !v); }}>
                      {editMode ? <><Check className="w-3.5 h-3.5" /> Bitir</> : <>✏️ Düzenle</>}
                    </Button>
                    <Button size="sm" variant="outline" className="gap-1.5" onClick={() => { navigator.clipboard?.writeText(revisedShown.replace(/==(.+?)==/g, "$1")); setCopied("md"); setTimeout(() => setCopied(""), 1800); }}>
                      {copied === "md" ? <><Check className="w-3.5 h-3.5" style={{ color: C.green }} /> Kopyalandı</> : <>Markdown</>}
                    </Button>
                    <Button size="sm" variant="outline" className="gap-1.5" onClick={() => { navigator.clipboard?.writeText(mdToHtml(revisedShown)); setCopied("html"); setTimeout(() => setCopied(""), 1800); }}>
                      {copied === "html" ? <><Check className="w-3.5 h-3.5" style={{ color: C.green }} /> Kopyalandı</> : <>HTML</>}
                    </Button>
                    <Button size="sm" variant="outline" disabled={zipBusy} className="gap-1.5" title="Word belgesi (görseller gömülü) + görsel dosyaları + meta.txt — tek ZIP" onClick={() => void downloadZip(revisedShown, fname)}>📄 {zipBusy ? "Hazırlanıyor…" : "Word + Görseller (ZIP)"}</Button>
                    {/* UYGULAMA KOLU: WordPress bağlıysa tek tıkla siteye yaz; sonrasında geri al görünür */}
                    {wpDurum.data?.bagli && (
                      <>
                        <Button size="sm" disabled={wpBusy} className="gap-1.5 bg-brand-500 hover:bg-brand-600" title="Başlık + içerik + SEO meta WordPress'e yazılır; önce canlı içerik yedeklenir" onClick={() => void wpUygula()}>
                          {wpBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null} Siteye Uygula (WP)
                        </Button>
                        {sonWpUygulama && (
                          <Button size="sm" variant="outline" disabled={wpBusy} className="gap-1.5 text-amber-600 border-amber-500/40" title="Uygulamadan önceki yedeğe geri döndür" onClick={() => void wpGeriAl()}>
                            ↩ Geri Al
                          </Button>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div>
                  <div className="text-xs font-medium text-muted-foreground mb-2 flex items-center gap-1.5">📄 Mevcut İçerik {d.existingContent && <span className="opacity-60">({d.existingContent.length} karakter)</span>}</div>
                  <div className="rounded-lg border p-3 max-h-[760px] overflow-y-auto" style={{ borderColor: "rgba(0,0,0,0.08)" }}>
                    {d.existingContent?.trim() ? <MiniMarkdown text={d.existingContent} /> : <span className="text-sm text-muted-foreground">(sayfanın çekilmiş içeriği boş / iskelet — yeniden taramada dolabilir)</span>}
                  </div>
                </div>
                <div>
                  <div className="text-xs font-medium mb-2 flex items-center gap-1.5" style={{ color: C.green }}>✨ Revize Edilmiş İçerik {revisedShown && <span className="opacity-60">({revisedShown.replace(/==/g, "").length} karakter)</span>}{editMode && <span className="text-[10px] px-1.5 py-0.5 rounded" style={{ background: `${C.amber}22`, color: C.amber }}>düzenleme modu</span>}</div>
                  {editMode ? (
                    <textarea value={revisedShown} onChange={(e) => setEdited(e.target.value)} spellCheck={false}
                      className="w-full rounded-lg border p-3 text-sm font-mono leading-relaxed bg-transparent outline-none" style={{ borderColor: "rgba(245,158,11,0.4)", height: 760 }} />
                  ) : (
                    <div className="rounded-lg border p-3 max-h-[760px] overflow-y-auto" style={{ borderColor: "rgba(34,197,94,0.3)", background: "rgba(34,197,94,0.03)" }}>
                      {revisedShown ? <MiniMarkdown text={revisedShown} /> : <span className="text-sm text-muted-foreground">Revize üretilemedi (AI motoru kotası gerekli olabilir). “Güçlendir”e tekrar bas.</span>}
                    </div>
                  )}
                </div>
              </div>

              {/* MANUEL REVİZE — kullanıcı isteği: "en altta elle revize yapabilelim".
                  Serbest talimat motora gider; yalnız istenen yer değişir, gerisi korunur. */}
              {d.revisedContent && (
                <div className="mt-4 rounded-lg border border-dashed p-3" style={{ borderColor: "rgba(245,158,11,0.5)" }}>
                  <div className="mb-1.5 text-[12.5px] font-semibold flex items-center gap-1.5">🪄 Manuel Revize <span className="font-normal text-muted-foreground">— ne değişsin istediğini yaz, motor yalnız o kısmı düzeltir (~1 token)</span></div>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <textarea value={revizeTalimat} onChange={(e) => setRevizeTalimat(e.target.value)} rows={5} maxLength={6000} spellCheck={false}
                      placeholder={"Örn: Fiyat bölümünü kısalt ve tabloyu koru · SSS bölümüne iyileşme süresiyle ilgili bir soru ekle · Girişteki mesajı daha net yaz\n\nBirden çok revizeyi madde madde yazabilirsin — motor hepsini tek geçişte uygular."}
                      className="flex-1 rounded-lg border p-2.5 text-[13px] bg-transparent outline-none resize-y dark:text-white/90 dark:placeholder:text-gray-500" style={{ borderColor: "rgba(0,0,0,0.12)" }} />
                    <Button className="shrink-0 self-start gap-1.5" disabled={revizeBusy || revizeTalimat.trim().length < 5} onClick={() => void manuelRevizeUygula()}>
                      {revizeBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}{revizeBusy ? "Uygulanıyor…" : "Revizeyi Uygula"}
                    </Button>
                  </div>
                  {revizeMesaj && <p className="mt-1.5 text-[12px]" style={{ color: revizeMesaj.startsWith("✓") ? C.green : C.red }}>{revizeMesaj}</p>}
                </div>
              )}
            </CardContent></Card>
              );
            })()}
          </>
        );
      })()}
    </div>
  );
}
